import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, CheckCircle, Circle, Loader2, ChevronDown, Lightbulb } from "lucide-react";
import { useTranslation } from "react-i18next";
import { fetchInspectionById } from "../services/inspectionService";
import { createInspection, captureDeviceInfo } from "../services/uploadService";
import { useShakeDetection } from "../hooks/useShakeDetection";
import { shakeDetectionService } from "../services/shakeDetectionService";
import { useBluetoothButton } from "../hooks/useBluetoothButton";
import { bluetoothButtonService } from "../services/bluetoothButtonService";
import { formatDuration, formatFileSize } from "../services/audioProcessingService";
import AppBarMenu from "./AppBarMenu";
import Logo from "./Logo";

// Module-level state to handle React StrictMode double-mount
// This ensures only one upload happens even when component mounts twice
let pendingUploadState = {
  inspectionId: null,
  status: null,
  blobId: null, // Unique ID for the current blob to detect new recordings
  subscribers: new Set(), // Functions to call when state updates
};

const notifySubscribers = () => {
  pendingUploadState.subscribers.forEach(fn => fn({ ...pendingUploadState }));
};

const resetPendingUpload = () => {
  pendingUploadState = {
    inspectionId: null,
    status: null,
    blobId: null,
    subscribers: new Set(),
  };
};

// Generate a unique ID for a blob (using size + lastModified as proxy)
const getBlobId = (blob) => {
  if (!blob) return null;
  return `${blob.size}-${blob.lastModified || Date.now()}`;
};

// Alert component
const Alert = ({ severity, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]}`}>
      {children}
    </div>
  );
};

// Accordion component
const Accordion = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-border rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-background hover:bg-gray-100 transition-colors cursor-pointer"
      >
        <span className="text-sm font-medium text-text-muted">{title}</span>
        <ChevronDown
          size={20}
          className={`text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      {isOpen && (
        <div className="p-4 bg-gray-50">
          {children}
        </div>
      )}
    </div>
  );
};

const Review = ({
  recordingData,
  onUpload,
  onReRecord,
  onBack,
  onLogout,
  onOpenEnrichmentSettings,
  isAdmin = false,
}) => {
  console.log('[Review] Component mounting with recordingData:', recordingData ? { vehicleId: recordingData.vehicleId, hasBlob: !!recordingData.blob, mimeType: recordingData.mimeType } : null);

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Format date using de-DE format consistently
  const formatDate = (date) => {
    if (!date) return '';
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const [inspectionId, setInspectionId] = useState(null);
  const [status, setStatus] = useState("PREPROCESSING");
  const [rawTranscript, setRawTranscript] = useState(null);
  const [processedTranscript, setProcessedTranscript] = useState(null);
  const [error, setError] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [shakeEnabled, setShakeEnabled] = useState(shakeDetectionService.isEnabled());
  const [bluetoothButtonEnabled, setBluetoothButtonEnabled] = useState(bluetoothButtonService.isEnabled());
  const [preprocessingStats, setPreprocessingStats] = useState(null);
  const [createdAt, setCreatedAt] = useState(null);
  const [processedAudioUrl, setProcessedAudioUrl] = useState(null);
  const pageLoadTimeRef = useRef(Date.now());
  const uploadCompletedRef = useRef(false); // Track if upload finished (for cleanup)
  const createdInspectionIdRef = useRef(null); // Track created inspection (for cleanup)

  const { vehicleId } = recordingData;

  // Shake detection to return to start
  useShakeDetection(
    shakeEnabled,
    () => {
      const timeSinceLoad = Date.now() - pageLoadTimeRef.current;
      if (timeSinceLoad >= 1000) {
        onBack();
      }
    }
  );

  // Listen for global shake setting changes
  useEffect(() => {
    const handleShakeSettingChange = (event) => {
      const { enabled } = event.detail;
      setShakeEnabled(enabled);
    };

    window.addEventListener("shakeSettingChanged", handleShakeSettingChange);
    return () => window.removeEventListener("shakeSettingChanged", handleShakeSettingChange);
  }, []);

  // Listen for Bluetooth button setting changes
  useEffect(() => {
    const handleBluetoothButtonSettingChange = (event) => {
      const { enabled } = event.detail;
      setBluetoothButtonEnabled(enabled);
    };

    window.addEventListener("bluetoothButtonSettingChanged", handleBluetoothButtonSettingChange);
    return () => window.removeEventListener("bluetoothButtonSettingChanged", handleBluetoothButtonSettingChange);
  }, []);

  // Bluetooth button detection hook
  useBluetoothButton(bluetoothButtonEnabled, () => {
    const timeSinceLoad = Date.now() - pageLoadTimeRef.current;
    if (timeSinceLoad >= 1000) {
      onBack();
    }
  });

  // Create audio URL from blob on mount
  useEffect(() => {
    if (recordingData && recordingData.blob) {
      const url = URL.createObjectURL(recordingData.blob);
      setAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [recordingData]);

  // Cleanup processed audio URL on unmount
  useEffect(() => {
    return () => {
      if (processedAudioUrl) {
        URL.revokeObjectURL(processedAudioUrl);
      }
    };
  }, [processedAudioUrl]);

  // Detect if this is a manifest flow (inspectionId provided but no blob)
  const isManifestFlow = recordingData?.inspectionId && !recordingData?.blob;

  // Auto-start preprocessing and upload when component mounts
  useEffect(() => {
    let isMounted = true;

    // If we have inspectionId but no blob, we're coming from manifest upload
    // Skip preprocessing/upload and go straight to polling
    if (isManifestFlow) {
      setInspectionId(recordingData.inspectionId);
      setCreatedAt(new Date());
      setStatus("FINALIZING");
      return;
    }

    // Generate a unique ID for this blob
    const currentBlobId = getBlobId(recordingData.blob);

    // Subscribe to module-level state updates (for StrictMode double-mount)
    const handleStateUpdate = (state) => {
      if (isMounted) {
        if (state.inspectionId) setInspectionId(state.inspectionId);
        if (state.status) setStatus(state.status);
      }
    };
    pendingUploadState.subscribers.add(handleStateUpdate);

    // Check if upload is already in progress for THIS blob (StrictMode remount)
    const isSameBlob = pendingUploadState.blobId === currentBlobId;
    const hasExistingUpload = pendingUploadState.blobId && isSameBlob;

    if (hasExistingUpload) {
      console.log('Review: Upload already in progress for this blob, subscribing to updates');
      // Sync current state
      if (pendingUploadState.inspectionId) setInspectionId(pendingUploadState.inspectionId);
      if (pendingUploadState.status) setStatus(pendingUploadState.status);
      return () => {
        isMounted = false;
        pendingUploadState.subscribers.delete(handleStateUpdate);
      };
    }

    // New blob - reset any stale state and start fresh
    if (pendingUploadState.blobId && !isSameBlob) {
      console.log('Review: New blob detected, resetting state');
      resetPendingUpload();
      pendingUploadState.subscribers.add(handleStateUpdate);
    }

    // Mark upload as in progress for this blob
    pendingUploadState.blobId = currentBlobId;

    const performPreprocessingAndUpload = async () => {
      console.log('[Review] Starting performPreprocessingAndUpload');
      try {
        // Step 1: Create inspection with device info (no input device for file uploads)
        console.log('[Review] Step 1: Creating inspection...');
        let currentInspectionId = null;
        try {
          // Pass "File Upload" as input device for file uploads (vs microphone for recordings)
          const deviceInfo = captureDeviceInfo("File Upload");
          const inspection = await createInspection(recordingData.vehicleId, deviceInfo);
          currentInspectionId = inspection.inspectionId;
          createdInspectionIdRef.current = currentInspectionId;

          // Update module-level state and notify subscribers
          pendingUploadState.inspectionId = currentInspectionId;
          notifySubscribers();

          if (isMounted) {
            setInspectionId(currentInspectionId);
            setCreatedAt(new Date());
            // Update URL to include inspection ID (makes it shareable/resumable)
            navigate(`/record/${currentInspectionId}`, { replace: true });
          }
          console.log(`Inspection created: ${currentInspectionId}`);
        } catch (createErr) {
          console.error("Failed to create inspection:", createErr);
          pendingUploadState.status = "FAILED";
                    notifySubscribers();
          if (isMounted) {
            setError(createErr.message || "Failed to create inspection");
            setStatus("FAILED");
          }
          return;
        }

        // Skip preprocessing for file uploads - decodeAudioData blocks the main thread
        // and prevents timeouts from firing. Upload raw file directly.
        console.log('[Review] Skipping preprocessing for file upload (blocking issue)');
        let processedBlob = recordingData.blob;

        // Step 2: Upload processed file
        console.log('[Review] Step 2: Uploading file...');
        pendingUploadState.status = "UPLOADING";
        notifySubscribers();
        if (isMounted) setStatus("UPLOADING");

        const uploadData = {
          ...recordingData,
          inspectionId: currentInspectionId,
          blob: processedBlob,
          originalBlob: recordingData.blob,
          uploadAsRaw: true,  // Backend will process with FFmpeg (silence removal)
        };

        const result = await onUpload(uploadData);

        if (result && result.inspectionId) {
          // Upload complete - uploadRecording already called finalize
          uploadCompletedRef.current = true;
          console.log(`Upload completed for inspection: ${currentInspectionId}`);

          // Set status to transcribing (finalize was called by uploadRecording)
          pendingUploadState.status = "TRANSCRIBING";
          notifySubscribers();
          if (isMounted) setStatus("TRANSCRIBING");
        } else {
          console.error("Upload completed but no inspectionId returned");
          pendingUploadState.status = "FAILED";
          notifySubscribers();
          if (isMounted) {
            setError("Upload completed but no inspectionId returned");
            setStatus("FAILED");
          }
          return;
        }
      } catch (err) {
        console.error("Upload failed:", err);
        pendingUploadState.status = "FAILED";
                notifySubscribers();
        if (isMounted) {
          setError(err.message || "Upload failed");
          setStatus("FAILED");
        }
      }
    };

    performPreprocessingAndUpload();

    return () => {
      isMounted = false;
      pendingUploadState.subscribers.delete(handleStateUpdate);
      // Note: We intentionally don't stop the upload here.
      // The upload continues in background and will finalize the inspection.
    };
  }, []);

  // Poll for inspection status every 3 seconds
  useEffect(() => {
    if (!inspectionId || status === "COMPLETED" || status === "FAILED") {
      return;
    }

    // Normalize backend status values to frontend expected values
    const normalizeStatus = (backendStatus) => {
      if (!backendStatus) return "TRANSCRIBING";
      const upper = backendStatus.toUpperCase();
      // Map various failure statuses to FAILED
      if (upper.includes("FAILED") || upper.includes("ERROR")) {
        return "FAILED";
      }
      // Map PROCESSING to TRANSCRIBING (same conceptual state)
      if (upper === "PROCESSING") {
        return "TRANSCRIBING";
      }
      return backendStatus;
    };

    const fetchStatus = async () => {
      try {
        const data = await fetchInspectionById(inspectionId);

        const normalizedStatus = normalizeStatus(data.status);
        setStatus(normalizedStatus);
        setRawTranscript(data.rawTranscript || null);
        setProcessedTranscript(data.processedTranscript || null);

        if (data.error) {
          setError(data.error);
        }
      } catch (err) {
        console.error("Error fetching inspection status:", err);
        if (err.message && !err.message.includes("404") && !err.message.includes("not found")) {
          setError(err.message);
        }
      }
    };

    fetchStatus();
    const pollInterval = setInterval(fetchStatus, 3000);

    return () => clearInterval(pollInterval);
  }, [inspectionId, status, isManifestFlow]);

  // Dynamic steps based on flow type
  const steps = useMemo(() => {
    if (isManifestFlow) {
      // Manifest flow - segments already uploaded, just waiting for backend processing
      return [
        { label: t('review.step_finalizing'), key: 'finalizing' },
        { label: t('review.step_enriching'), key: 'enriching' },
        { label: t('review.step_done'), key: 'done' },
      ];
    } else {
      // Original flow with preprocessing
      return [
        { label: t('review.step_preprocessing'), key: 'preprocessing' },
        { label: t('review.step_uploading'), key: 'uploading' },
        { label: t('review.step_transcribing'), key: 'transcribing' },
        { label: t('review.step_enriching'), key: 'enriching' },
        { label: t('review.step_done'), key: 'done' },
      ];
    }
  }, [isManifestFlow, t]);

  // Calculate progress step
  // Progress is monotonic - once we pass a step, we never go back
  const getProgressStep = () => {
    if (isManifestFlow) {
      // Manifest flow: 3 steps (Finalizing, Enriching, Done)
      // Step 4 means all complete (shows checkmark on step 3)
      if (status === "FAILED") {
        if (processedTranscript) return 3;
        if (rawTranscript) return 2;
        return 1;
      }
      if (status === "COMPLETED" && processedTranscript) return 4;
      if (processedTranscript) return 3;
      if (rawTranscript) return 2;
      // Finalizing (waiting for transcription to complete)
      return 1;
    }

    // Original blob flow: 5 steps (Preprocessing, Uploading, Transcribing, Enhancing, Done)
    // Step 6 means all complete (shows checkmark on step 5)
    // Failed state - return current step to show error
    if (status === "FAILED") {
      if (processedTranscript) return 5;
      if (rawTranscript) return 4;
      if (inspectionId) return 3;
      return 2;
    }

    // Step 5 complete: we have processed transcript and status is COMPLETED
    if (status === "COMPLETED" && processedTranscript) return 6;

    // Step 5 (done but not yet marked complete): have processed transcript
    if (processedTranscript) return 5;

    // Step 4 (enhancing): have raw transcript, waiting for processed
    if (rawTranscript) return 4;

    // Step 3 (transcribing): transcription in progress
    if (status === "TRANSCRIBING" || status === "FINALIZING") return 3;

    // Step 2 (uploading): uploading in progress
    if (status === "UPLOADING") return 2;

    // Step 1 (preprocessing): initial state or preprocessing
    if (status === "PREPROCESSING" || !status) return 1;

    // Step 1 (preprocessing): initial state
    return 1;
  };

  const isFailed = status === "FAILED";

  const progressStep = getProgressStep();

  // Progress step component
  const ProgressStep = ({ step, current, label, failed }) => {
    const isComplete = current > step;
    const isCurrent = current === step;
    const isFailedStep = failed && isCurrent;

    return (
      <div className="flex items-center gap-3 mb-3">
        {isFailedStep ? (
          <Circle size={28} className="text-error flex-shrink-0" />
        ) : isComplete ? (
          <CheckCircle size={28} className="text-success flex-shrink-0" />
        ) : isCurrent ? (
          <Loader2 size={28} className="text-primary animate-spin flex-shrink-0" />
        ) : (
          <Circle size={28} className="text-border flex-shrink-0" />
        )}
        <span
          className={`text-base ${
            isFailedStep ? 'text-error font-semibold' :
            isCurrent ? 'text-primary font-semibold' :
            isComplete ? 'text-success' : 'text-text-light'
          }`}
        >
          {step}. {label}
        </span>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* App Bar */}
      <header className="app-bar">
        <button onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80">
          <Logo size={40} />
        </button>
        <h1 className="flex-1 text-white font-normal text-xl ml-3">
          {vehicleId || t('review.title')}
        </h1>
        {onLogout && (
          <AppBarMenu onLogout={onLogout} isAdmin={isAdmin} onOpenEnrichmentSettings={onOpenEnrichmentSettings} />
        )}
      </header>

      {/* Main Content */}
      <main className="w-full px-4 lg:px-8 py-8">
        <div className="card-elevated p-6">
          {/* Title with inspection info */}
          <div className="mb-6 pb-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-lg font-bold text-text">
                  {vehicleId}
                </h1>
                <p className="text-sm text-text-muted">
                  {inspectionId && (
                    <span className="font-mono">#{inspectionId.slice(-7)}</span>
                  )}
                  {createdAt && (
                    <span className="ml-2">
                      · {formatDate(createdAt)}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Progress Visualization (dynamic based on flow type) */}
          <div className="mb-6">
            {steps.map((step, index) => (
              <ProgressStep
                key={step.key}
                step={index + 1}
                current={progressStep}
                label={step.label}
                failed={isFailed}
              />
            ))}
          </div>

          {/* Pre-processing Stats (shown after preprocessing completes) */}
          {preprocessingStats && preprocessingStats.analyzed && (
            <div className={`mb-4 p-3 rounded-lg text-sm ${
              preprocessingStats.silenceRemoved > 0
                ? 'bg-success/10 border border-success/20'
                : 'bg-surface border border-border'
            }`}>
              {preprocessingStats.silenceRemoved > 0 ? (
                <>
                  <span className="text-success font-medium">
                    {t('preprocess.saved')} {formatDuration(preprocessingStats.silenceRemoved)}
                  </span>
                  <span className="text-text-muted ml-2">
                    ({formatDuration(preprocessingStats.originalDuration)} → {formatDuration(preprocessingStats.processedDuration)})
                  </span>
                </>
              ) : (
                <span className="text-text-muted">
                  {formatDuration(preprocessingStats.originalDuration)} · {formatFileSize(preprocessingStats.originalSize)}
                  {preprocessingStats.silencePercentage > 5 && (
                    <> · {Math.round(preprocessingStats.silencePercentage)}% {t('preprocess.silence')}</>
                  )}
                </span>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && status === "FAILED" && (
            <Alert severity="error" className="mb-4">
              {error}
            </Alert>
          )}

          {/* Raw Transcript */}
          {rawTranscript && (
            <div className="mb-4 border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-gray-100 border-b border-border">
                <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                  {t('review.raw_transcript')}
                </span>
              </div>
              <div className="p-4">
                <p className="text-sm text-text-muted whitespace-pre-wrap leading-relaxed">
                  {rawTranscript}
                </p>
              </div>
            </div>
          )}

          {/* Processed Transcript */}
          {status === "COMPLETED" && !rawTranscript ? (
            <Alert severity="warning" className="mb-4">
              {t('review.empty_transcript_error')} {t('review.empty_transcript_hint')}
            </Alert>
          ) : (
            processedTranscript && (
              <div className="mb-4 border border-primary rounded-lg overflow-hidden">
                <div className="px-4 py-2 bg-primary">
                  <span className="text-xs font-semibold text-white uppercase tracking-wider">
                    {t('review.enhancement')}
                  </span>
                </div>
                <div className="p-4">
                  <p className="text-sm text-text whitespace-pre-wrap leading-relaxed">
                    {processedTranscript}
                  </p>
                </div>
              </div>
            )
          )}

          {/* Continue Button */}
          <button
            onClick={() => navigate('/')}
            className="w-full bg-primary hover:bg-primary-dark active:scale-[0.98] text-white font-bold text-xl py-5 px-6 rounded-lg flex items-center justify-center gap-3 transition-colors cursor-pointer"
          >
            <span>{t('review.continue')}</span>
            <ArrowRight size={32} />
          </button>

          {/* Hint Box */}
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <Lightbulb size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">
              {t('review.hint_continue')}{" "}
              <a
                href="https://holocron.ai.slalomlab.com"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-primary underline"
              >
                holocron.ai.slalomlab.com
              </a>{" "}
              {t('review.hint_retrieve')}
            </p>
          </div>

          {/* Expert Section - show when reaching the "Done" step */}
          {progressStep >= steps.length && (
            <div className="mt-4">
              <Accordion title={t('review.expert_section')}>
                {/* Processed Audio Player (silence removed - what was uploaded) */}
                {processedAudioUrl && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-text-muted mb-2">
                      {t('review.processed_recording')}
                    </p>
                    <audio controls className="w-full" src={processedAudioUrl}>
                      {t('review.audio_not_supported')}
                    </audio>
                  </div>
                )}

                {/* Raw Audio Player (original recording) */}
                {audioUrl && (
                  <div className="mb-4">
                    <p className="text-sm font-semibold text-text-muted mb-2">
                      {t('review.raw_recording')}
                    </p>
                    <audio controls className="w-full" src={audioUrl}>
                      {t('review.audio_not_supported')}
                    </audio>
                  </div>
                )}

                {/* Portal Link */}
                <p className="text-sm text-text-muted text-center">
                  {t('review.portal_visit')}{" "}
                  <a
                    href="https://holocron.ai.slalomlab.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-primary underline"
                  >
                    holocron.ai.slalomlab.com
                  </a>{" "}
                  {t('review.portal_overview')}
                </p>
              </Accordion>
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default Review;
